/**
 *
 * TemplatePreviewPage
 *
 */

import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, flatMap, negate, isNil, reject, find } from 'lodash';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router-dom';
import injectSaga from 'utils/injectSaga';
import injectReducer from 'utils/injectReducer';
import { makeSelectCurrentRoute } from 'containers/App/selectors';

import { Button, Layout, Row, Col, Tag, Divider } from 'antd';
import Pficon from 'components/Pficon';
import QuestionsListViewOnly from 'containers/TemplatePreviewPage/QuestionsListViewOnly/index';
import CreateSurveyModal from 'containers/CreateSurveyModal/Loadable';
import { SURVEY_FLOW } from 'containers/CreateSurveyModal/constants';
import {
  defaultSurveyType,
  defaultPeeqCategoryType,
} from 'containers/CreateSurveyModal/utils';

import getTargetingData from 'containers/SurveyPage/hooks/useGetTargetingData';
import getTargetingPeeqData from 'containers/SurveyPage/hooks/useGetTargetingPeeqData';
import getQuestionnaireData from 'containers/SurveyPage/hooks/useGetQuestionnaireData';
import {
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
} from 'containers/Survey/constants';

import SURVEY_GENERATORS from './TemplateFlows/ComplexFlow/SurveyGenerators';

import ComplexFlow from './TemplateFlows/ComplexFlow';

import makeSelectTemplatePreviewPage, {
  makeSelectTemplateVariableQuestionGroups,
} from './selectors';
import { fetchTemplatePreview, createFromTemplate } from './actions';
import reducer from './reducer';
import saga from './saga';
import messages from './messages';
import {
  hasFieldError,
  getDefaultVariableValue,
  overrideTemplateBasedOnPerk,
  doesSurveyTypeSupportScheduling,
} from './utils';
import { TEMPLATE_TAG_COLOR_MAP } from '../TemplatesPage/constants';
import { getTransformedTagName } from '../TemplatesPage/utils';
import {
  makeSelectUserAccountSelected,
  makeSelectHasPerk,
  makeSelectIsProdegeUser,
  makeSelectedAccountHasRegularSurvey,
  makeSelectedAccountHasDistributionLink,
  makeSelectedAccountHasPeeqSurvey,
  makeSelectIsPollfishAdmin,
} from '../User/selectors';

const LayoutSider = Layout.Sider;
const LayoutContent = Layout.Content;

const isNotNil = negate(isNil);

const TemplatePreviewPage = (props) => {
  const {
    route,
    selectedAccount,
    userHideAgeUnderEighteen,
    isProdegeUser,
    hasSchedulingPerk,
    regularSurvey,
    hasDistributionLinkPerk,
    hasPeeqSurveyPerk,
    isUserAdmin,
  } = props;
  const { categoryId = '', folderId = '' } = queryString.parse(route.search);
  let templatesUrl = '/templates';
  if (categoryId || folderId) {
    const params = {};
    if (categoryId) {
      templatesUrl += `/${categoryId}/`;
    }
    if (folderId) {
      params.folderId = folderId;
      templatesUrl += `?${queryString.stringify(params)}`;
    }
  }

  const [showModal, setShowModal] = useState(false);
  const [surveyCreationFlow, setSurveyCreationFlow] = useState();
  const [templatePage, setTemplatePage] = useState(0);
  const [templateVariableValues, setTemplateVariableValues] = useState([]);
  const [selectedSurveyType, setSelectedSurveyType] = useState(
    defaultSurveyType({
      surveyCreationFlow,
      isProdegeUser,
      regularSurvey,
      hasDistributionLinkPerk,
      hasPeeqSurveyPerk,
    }),
  );
  const [selectedPeeqCategoryType, setSelectedPeeqCategoryType] = useState(
    defaultPeeqCategoryType(isProdegeUser),
  );

  useEffect(() => {
    props.onFetchTemplatePreview(props.match.params.templateId);
  }, []);

  const {
    template = {},
    nodes = [],
    screeningQuestions = [],
    creatingSurvey,
    fetchedTemplate = false,
  } = props.templatePreviewPage;
  const { templateVariableQuestionGroups = [], onCreateFromTemplate } = props;

  const canUseTemplateRegular = get(template, 'canUse.forRegular', false);
  const canUseTemplateDistributionLink = get(
    template,
    'canUse.forDistributionLink',
    false,
  );
  const canUseTemplatePeeq = get(template, 'canUse.forPeeq', false);
  const canUseTemplate =
    canUseTemplateRegular ||
    canUseTemplateDistributionLink ||
    canUseTemplatePeeq;

  const onChangeSurveyType = useCallback(
    (newSurveyType, peeqCategoryType) => {
      setSelectedSurveyType(newSurveyType);
      setSelectedPeeqCategoryType(peeqCategoryType);
    },
    [setSelectedSurveyType],
  );

  const initializeTemplateVariableValues = useCallback(() => {
    if (templateVariableQuestionGroups.length) {
      const newValues = templateVariableQuestionGroups.map((templateGroup) => ({
        ...templateGroup,
        variableQuestions: templateGroup.variableQuestions.map(
          (variableQuestion) => {
            const questionWithValue = {
              ...variableQuestion,
              isPristine: true,
              value: getDefaultVariableValue(
                variableQuestion,
                hasSchedulingPerk,
              ),
            };

            return {
              ...questionWithValue,
              hasError: hasFieldError(questionWithValue),
            };
          },
        ),
      }));

      setTemplateVariableValues(newValues);
    } else {
      setTemplateVariableValues([]);
    }
  }, [templateVariableQuestionGroups]);

  const updateVariableValues = useCallback(
    ({ questionGroupIndex, questionIndex, value }) => {
      setTemplateVariableValues((prevTemplateVariableValues) => {
        const newValues = prevTemplateVariableValues.map(
          (currentGroup, currentGroupIndex) => {
            if (currentGroupIndex === questionGroupIndex) {
              return {
                ...currentGroup,
                variableQuestions: currentGroup.variableQuestions.map(
                  (question, currentQuestionIndex) => {
                    if (currentQuestionIndex === questionIndex) {
                      const hasError = hasFieldError({
                        ...question,
                        value,
                        samePageQuestions: currentGroup.variableQuestions,
                      });

                      return {
                        ...question,
                        hasError,
                        value,
                      };
                    }
                    return question;
                  },
                ),
              };
            }
            return currentGroup;
          },
        );
        return newValues;
      });
    },
    [],
  );

  const showCreateSurveyModal = useCallback(
    (surveyFlow = SURVEY_FLOW.REGULAR) => {
      document.body.classList.add('create-survey-modal-open');
      initializeTemplateVariableValues();
      setSurveyCreationFlow(surveyFlow);
      setShowModal(true);
    },
    [initializeTemplateVariableValues],
  );

  const closeModal = useCallback(() => {
    document.body.classList.remove('create-survey-modal-open');
    setShowModal(false);
    setTemplatePage(0);
    setSurveyCreationFlow(SURVEY_FLOW.REGULAR);
  }, []);

  const activeTemplateVariablesValues = useMemo(() => {
    const surveyTypeSupportScheduling = doesSurveyTypeSupportScheduling(
      selectedSurveyType,
      selectedPeeqCategoryType,
    );

    if (!surveyTypeSupportScheduling) {
      return templateVariableValues
        .map((variableGroup) => {
          const variables = variableGroup.variableQuestions.filter(
            ({ name }) => name !== 'defaultRecurrence',
          );
          if (!variables.length) return null;
          return {
            ...variableGroup,
            variableQuestions: variables,
          };
        })
        .filter(isNotNil);
    }

    return templateVariableValues;
  }, [templateVariableValues, selectedSurveyType, selectedPeeqCategoryType]);

  const hasStepValidationErrors = () => {
    const hasTemplateVariables = activeTemplateVariablesValues.length > 0;
    if (!hasTemplateVariables) return false;

    const { variableQuestions } = activeTemplateVariablesValues[templatePage];

    const hasStepErrors = variableQuestions.some((question) =>
      hasFieldError({ ...question, samePageQuestions: variableQuestions }),
    );

    return hasStepErrors;
  };

  const setPristineFalse = useCallback(() => {
    setTemplateVariableValues((prevTemplateVariableValues) =>
      prevTemplateVariableValues.map((variablesGroup, variableGroupIndex) => {
        if (variableGroupIndex === templatePage) {
          return {
            ...variablesGroup,
            variableQuestions: variablesGroup.variableQuestions.map(
              (question) => ({
                ...question,
                isPristine: false,
              }),
            ),
          };
        }
        return variablesGroup;
      }),
    );
  }, [templatePage]);

  const createTemplateSurvey = useCallback(
    ({
      surveyType,
      surveyName,
      nextStep,
      selectedCountry,
      totalCompletes,
      peeqCategoryType,
    }) => {
      if (nextStep < templatePage) {
        setTemplatePage(nextStep);
        return;
      }

      const hasValidationErrors = hasStepValidationErrors();

      if (hasValidationErrors) {
        setPristineFalse();
        return;
      }

      if (nextStep < activeTemplateVariablesValues.length) {
        setTemplatePage(nextStep);
        return;
      }

      const variables = flatMap(
        activeTemplateVariablesValues,
        (variableQuestionGroup) =>
          variableQuestionGroup.variableQuestions.map(({ name, value }) => ({
            name,
            value,
          })),
      );

      const generatedSurveyData = SURVEY_GENERATORS[
        props.templatePreviewPage.template.generator
      ]({
        template: overrideTemplateBasedOnPerk(
          template,
          userHideAgeUnderEighteen,
        ),
        variables,
        surveyType,
        peeqCategoryType,
      });
      const useSelector = (selector) => selector(generatedSurveyData.state);
      const usePrevious = (_) => _;
      const overrideHooks = { useSelector, usePrevious };
      const overrideSurveyType =
        surveyType === SURVEY_TYPE_PEEQ ? SURVEY_TYPE_PEEQ : undefined;

      const questionnaireData = getQuestionnaireData({}, overrideHooks);
      const targetingData = {
        ...getTargetingData({ overrideSurveyType }, overrideHooks),
        ...getTargetingPeeqData({ overrideSurveyType }, overrideHooks),
      };

      const storeObject = {
        targetingData,
        questionnaireData,
        surveyId: undefined,
        isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
        isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      };
      const { defaultRecurrence } = generatedSurveyData;

      onCreateFromTemplate({
        surveyName,
        surveyType,
        templateId: props.match.params.templateId,
        storeObject,
        defaultRecurrence,
        selectedCountry,
        totalCompletes,
        peeqCategoryType,
      });
    },
    [
      templatePage,
      activeTemplateVariablesValues,
      props.templatePreviewPage.template.generator,
      props.match.params.templateId,
      setTemplatePage,
      hasStepValidationErrors,
      setPristineFalse,
      onCreateFromTemplate,
      template,
      userHideAgeUnderEighteen,
    ],
  );

  const renderQuestions = useCallback(
    (qNodes, screeningNodes = []) => (
      <QuestionsListViewOnly
        nodes={qNodes}
        screeningQuestions={screeningNodes}
      />
    ),
    [nodes, screeningQuestions],
  );

  const templateVariablesWizard = useMemo(
    () => (
      <ComplexFlow
        templateVariableValues={activeTemplateVariablesValues}
        templatePage={templatePage}
        updateVariableValues={updateVariableValues}
        hasSchedulingPerk={hasSchedulingPerk}
      />
    ),
    [
      templatePage,
      activeTemplateVariablesValues,
      updateVariableValues,
      hasSchedulingPerk,
    ],
  );
  const normalTags = reject(template.tags, ['name', 'Tracker']);
  const trackerTag = find(template.tags, ['name', 'Tracker']);

  return (
    <Layout className="reset-ant-collapse template-page">
      <Link
        className="position-fixed"
        style={{
          zIndex: 10,
          top: 14,
          right: 18,
        }}
        to={templatesUrl}
      >
        <Pficon
          className="color-grey-10 text-weight-bold"
          type="close"
          style={{ fontSize: '20px' }}
        />
      </Link>
      <Layout>
        <LayoutSider
          collapsible
          collapsed={false}
          trigger={null}
          width={390}
          theme="light"
          className={classnames(
            'pf-sider',
            'pf-sider--h-stretch',
            'pf-sider--between',
            'overflow-vertical-auto',
            'padding-vertical-md',
            'padding-horizontal-lg',
          )}
        >
          <Row className="h-full">
            <Col className="h-full w-full">
              <div className="flex flex-col flex-justify-between h-full">
                <div>
                  <Link to={templatesUrl}>
                    <Pficon
                      className="color-grey-10 text-weight-bold"
                      type="arrow-thin-left"
                    />
                  </Link>

                  <h1 className="color-grey-10 text-size-h3 margin-top-lg margin-btm-lg text-weight-bold">
                    {template.name}
                  </h1>
                  <p className="text-size-h4">{template.description}</p>
                  {template.pros && (
                    <ul className="color-grey-10 list-style-none padding-none">
                      {template.pros.map((pro) => (
                        <li className="flex" key={pro}>
                          <Pficon
                            className="color-green-7 margin-right-xs text-size-h2 position-relative"
                            type="check-material"
                            style={{ lineHeight: '24px', top: '-5px' }}
                          />
                          <p className="text-size-h4">{pro}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  {template.tags && template.tags.length > 0 && (
                    <div>
                      <h3 className="color-grey-10 text-size-h4 margin-top-md margin-btm-none text-weight-bold">
                        <FormattedMessage {...messages.researchMethod} />
                      </h3>
                      <div className="flex flex-wrap flex-items-center margin-top-xxs">
                        {normalTags.map((tag) => (
                          <Tag
                            key={tag.id}
                            className="margin-horizontal-none border-none ant-tag-no-hover border-radius-level-1 color-grey-10"
                            color={
                              TEMPLATE_TAG_COLOR_MAP[tag.name] ||
                              TEMPLATE_TAG_COLOR_MAP.unknown
                            }
                            style={{
                              lineHeight: '22px',
                              marginRight: 6,
                            }}
                          >
                            {getTransformedTagName(tag.name)}
                          </Tag>
                        ))}
                        {trackerTag && (
                          <Fragment>
                            {Boolean(normalTags.length) && (
                              <Divider
                                type="vertical"
                                className="margin-none"
                                style={{ height: 16, marginRight: 6 }}
                              />
                            )}
                            <Tag
                              key={trackerTag.id}
                              className="margin-horizontal-none border-none ant-tag-no-hover border-radius-level-1 color-grey-10"
                              color={
                                TEMPLATE_TAG_COLOR_MAP[trackerTag.name] ||
                                TEMPLATE_TAG_COLOR_MAP.unknown
                              }
                              style={{
                                lineHeight: '22px',
                                marginRight: 6,
                              }}
                            >
                              {getTransformedTagName(trackerTag.name)}
                            </Tag>
                          </Fragment>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {fetchedTemplate && canUseTemplate && (
                    <div
                      className="margin-top-lg margin-horizontal-auto"
                      style={{ width: '180px' }}
                    >
                      <Button
                        type="primary"
                        className="w-full"
                        style={{ height: '40px' }}
                        onClick={() =>
                          showCreateSurveyModal(SURVEY_FLOW.TEMPLATE)
                        }
                      >
                        <FormattedMessage {...messages.chooseTemplate} />
                      </Button>
                      <div className="text-align-center text-size-h4 w-full margin-top-xxs margin-btm-lg">
                        <button
                          className={classnames(
                            'color-grey-10 color-grey-10-hover border-none',
                            'padding-xxs margin-top-xxs cursor-pointer',
                          )}
                          type="button"
                          onClick={() =>
                            showCreateSurveyModal(SURVEY_FLOW.REGULAR)
                          }
                        >
                          <FormattedMessage {...messages.startFromScratch} />
                        </button>
                      </div>
                    </div>
                  )}
                  {fetchedTemplate && !canUseTemplate && (
                    <div className="margin-top-lg margin-btm-xl">
                      <div className="margin-btm-sm flex flex-justify-center">
                        <Link
                          style={{
                            height: '40px',
                            lineHeight: '40px',
                            width: '180px',
                          }}
                          to="/account/subscriptions"
                          className={classnames(
                            'background-blue-6 border-radius-level-1 color-white color-white-hover',
                            'padding-horizontal-lg',
                            'text-size-h4',
                            'text-align-center',
                          )}
                        >
                          <FormattedMessage {...messages.upgradeYourPlan} />
                        </Link>
                      </div>
                      <span className="color-grey-9 text-size-h4 text-align-center w-full display-block">
                        <FormattedMessage {...messages.featureUnavailable} />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </LayoutSider>
        <LayoutContent
          className="template-preview-questions overflow-auto"
          style={{ height: '100vh', backgroundColor: '#eef3f7' }}
        >
          <Row
            className="padding-xl margin-horizontal-auto"
            style={{ maxWidth: '820px' }}
          >
            <Col className="margin-top-xl w-full">
              {renderQuestions(nodes, screeningQuestions)}
            </Col>
          </Row>
        </LayoutContent>
      </Layout>
      <CreateSurveyModal
        key={selectedAccount?.id}
        isOpen={showModal}
        closeModal={closeModal}
        loading={creatingSurvey}
        defaultSurveyName={
          surveyCreationFlow === SURVEY_FLOW.TEMPLATE ? template.name : ''
        }
        surveyCreationFlow={surveyCreationFlow}
        onChangeSurveyType={onChangeSurveyType}
        externalContentStep={templateVariablesWizard}
        externalSubmit={createTemplateSurvey}
        externalContentStepCount={activeTemplateVariablesValues.length}
        externalContentCurrentStep={templatePage}
        canUseTemplate={canUseTemplate}
        canUseTemplateRegular={canUseTemplateRegular}
        canUseTemplateDistributionLink={canUseTemplateDistributionLink}
        canUseTemplatePeeq={canUseTemplatePeeq}
        isProdegeUser={isProdegeUser}
        isUserAdmin={isUserAdmin}
      />
    </Layout>
  );
};

TemplatePreviewPage.propTypes = {
  templatePreviewPage: PropTypes.object,
  match: PropTypes.object,
  params: PropTypes.object,
  templateId: PropTypes.string,
  onFetchTemplatePreview: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  templatePreviewPage: makeSelectTemplatePreviewPage,
  templateVariableQuestionGroups: makeSelectTemplateVariableQuestionGroups,
  route: makeSelectCurrentRoute,
  selectedAccount: makeSelectUserAccountSelected,
  userHideAgeUnderEighteen: makeSelectHasPerk('hide1617AgeRange'),
  isProdegeUser: makeSelectIsProdegeUser,
  hasSchedulingPerk: makeSelectHasPerk('scheduling'),
  regularSurvey: makeSelectedAccountHasRegularSurvey,
  hasDistributionLinkPerk: makeSelectedAccountHasDistributionLink,
  hasPeeqSurveyPerk: makeSelectedAccountHasPeeqSurvey,
  isUserAdmin: makeSelectIsPollfishAdmin,
});

function mapDispatchToProps(dispatch) {
  return {
    onFetchTemplatePreview: (templateId) =>
      dispatch(fetchTemplatePreview(templateId)),
    onCreateFromTemplate: ({
      surveyName,
      surveyType,
      templateId,
      storeObject,
      defaultRecurrence,
      selectedCountry,
      totalCompletes,
      peeqCategoryType,
    }) =>
      dispatch(
        createFromTemplate({
          surveyName,
          surveyType,
          templateId,
          storeObject,
          defaultRecurrence,
          selectedCountry,
          totalCompletes,
          peeqCategoryType,
        }),
      ),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({ key: 'templatePreviewPage', reducer });
const withSaga = injectSaga({ key: 'templatePreviewPage', saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
  withRouter,
)(TemplatePreviewPage);
